import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/PageHeader';
import { toast } from 'sonner';
import { ElencoAllegati } from '@/components/VisualizzatoreDocumento';
import { Bell, Plus, Trash2, Send, Pencil, Archive, AlertTriangle, Calendar, Clock, Paperclip, MessageSquare } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Communication types
const COMM_TYPES = {
  AVVISO: { label: 'Avviso', color: 'bg-blue-100 text-blue-800' },
  URGENTE: { label: 'Urgente', color: 'bg-red-100 text-red-800' },
  LAVORI: { label: 'Lavori', color: 'bg-yellow-100 text-yellow-800' },
  SCADENZA: { label: 'Scadenza', color: 'bg-orange-100 text-orange-800' },
  ASSEMBLEA: { label: 'Assemblea', color: 'bg-purple-100 text-purple-800' },
  ALTRO: { label: 'Altro', color: 'bg-gray-100 text-gray-800' }
};

// Status labels
const COMM_STATUS = {
  DRAFT: { label: 'Bozza', color: 'bg-gray-100 text-gray-800' },
  PUBLISHED: { label: 'Pubblicato', color: 'bg-green-100 text-green-800' },
  ARCHIVED: { label: 'Archiviato', color: 'bg-slate-100 text-slate-800' }
};

export const Communications = () => {
  const { token, selectedCondominium } = useAuth();
  const userRole = selectedCondominium?.role || 'RESIDENT';
  const condoId = selectedCondominium?.condominium?.id;
  
  // State
  const [communications, setCommunications] = useState([]);
  const [selectedComm, setSelectedComm] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [isNewCommOpen, setIsNewCommOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [commInModifica, setCommInModifica] = useState(null);     // id della bozza che si sta modificando
  
  // Form state
  const [commForm, setCommForm] = useState({
    title: '',
    content: '',
    comm_type: 'AVVISO',
    priority: 0,
    expiry_date: ''
  });

  // Fetch functions
  const fetchCommunications = useCallback(async () => {
    if (!condoId) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/communications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setCommunications(await res.json());
    } catch (error) {
      console.error('Error fetching communications:', error);
    }
    setLoading(false);
  }, [condoId, token]);

  useEffect(() => {
    fetchCommunications();
  }, [fetchCommunications]);

  // Create communication
  const handleCreateComm = async (e) => {
    e.preventDefault();
    try {
      const modifica = Boolean(commInModifica);
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/communications${modifica ? `/${commInModifica}` : ''}`, {
        method: modifica ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(commForm)
      });
      if (res.ok) {
        toast.success(modifica ? 'Bozza aggiornata' : 'Comunicazione creata');
        setIsNewCommOpen(false);
        setCommInModifica(null);
        setCommForm({ title: '', content: '', comm_type: 'AVVISO', priority: 0, expiry_date: '' });
        fetchCommunications();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore creazione');
      }
    } catch (error) {
      toast.error('Errore creazione');
    }
  };

  // Modifica di una bozza: riapre la finestra di creazione con i dati gia' scritti
  const apriModifica = (comm) => {
    setCommInModifica(comm.id);
    setCommForm({
      title: comm.title || '', content: comm.content || '', comm_type: comm.comm_type || 'AVVISO',
      priority: comm.priority || 0, expiry_date: (comm.expiry_date || '').slice(0, 10)
    });
    setIsNewCommOpen(true);
  };

  // Publish communication
  const handlePublish = async (commId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/communications/${commId}/publish`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Comunicazione pubblicata');
        fetchCommunications();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore pubblicazione');
      }
    } catch (error) {
      toast.error('Errore pubblicazione');
    }
  };

  // Archive communication
  const handleArchive = async (commId) => {
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/communications/${commId}/archive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Comunicazione archiviata');
        fetchCommunications();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore archiviazione');
      }
    } catch (error) {
      toast.error('Errore archiviazione');
    }
  };

  // Delete communication
  const handleDelete = async (commId) => {
    if (!window.confirm('Eliminare questa comunicazione?')) return;
    try {
      const res = await fetch(`${API_URL}/api/condominiums/${condoId}/communications/${commId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Comunicazione eliminata');
        fetchCommunications();
        setIsDetailOpen(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Errore eliminazione');
      }
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  // Format helpers
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  const canEdit = userRole === 'ADMIN' || userRole === 'ACCOUNTANT';
  const canPublish = userRole === 'ADMIN';

  // Group communications by status
  const publishedComms = communications.filter(c => c.status === 'PUBLISHED' || c.status === 'SENT');   // «Inviato» = spedita dal Centro Comunicazioni: resta in bacheca
  const draftComms = communications.filter(c => c.status === 'DRAFT');
  const archivedComms = communications.filter(c => c.status === 'ARCHIVED');

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader title="Comunicazioni" subtitle="Bacheca e avvisi condominiali" />
      {canEdit && (
        <div className="flex flex-wrap gap-2 justify-end">
          <Dialog open={isNewCommOpen} onOpenChange={(aperto) => { setIsNewCommOpen(aperto); if (!aperto) { setCommInModifica(null); } }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                Nuova Comunicazione
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{commInModifica ? 'Modifica bozza' : 'Nuova Comunicazione'}</DialogTitle>
                <DialogDescription>{commInModifica ? 'Correggi il testo e salva: la bozza non e\' ancora visibile ai condomini' : 'Crea un nuovo avviso per la bacheca'}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateComm} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Tipo *</Label>
                    <Select
                      value={commForm.comm_type}
                      onValueChange={(v) => setCommForm({ ...commForm, comm_type: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(COMM_TYPES).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Priorità</Label>
                    <Select
                      value={String(commForm.priority)}
                      onValueChange={(v) => setCommForm({ ...commForm, priority: parseInt(v) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Normale</SelectItem>
                        <SelectItem value="1">Alta</SelectItem>
                        <SelectItem value="2">Urgente</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Titolo *</Label>
                  <Input
                    value={commForm.title}
                    onChange={(e) => setCommForm({ ...commForm, title: e.target.value })}
                    placeholder="Oggetto della comunicazione"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Contenuto *</Label>
                  <Textarea
                    value={commForm.content}
                    onChange={(e) => setCommForm({ ...commForm, content: e.target.value })}
                    placeholder="Testo della comunicazione..."
                    rows={5}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Data Scadenza</Label>
                  <Input
                    type="date"
                    value={commForm.expiry_date}
                    onChange={(e) => setCommForm({ ...commForm, expiry_date: e.target.value })}
                  />
                  <p className="text-xs text-muted-foreground">
                    Dopo questa data la comunicazione resta visibile ai condomini, con l'indicazione «scaduta». Lascia vuoto se non scade.
                  </p>
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setIsNewCommOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit">{commInModifica ? 'Salva modifiche' : 'Crea Bozza'}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        )}

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Caricamento...</div>
      ) : communications.length === 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center py-8 text-muted-foreground">
              <Bell className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nessuna comunicazione</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Published Communications */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Bell className="h-5 w-5 text-green-600" />
              Comunicazioni Attive ({publishedComms.length})
            </h2>
            {publishedComms.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-center text-muted-foreground">Nessuna comunicazione attiva</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {publishedComms.map((comm) => {
                  const typeInfo = COMM_TYPES[comm.comm_type] || COMM_TYPES.ALTRO;
                  return (
                    <Card key={comm.id} className={comm.priority >= 2 ? 'border-red-200 bg-red-50' : comm.priority >= 1 ? 'border-yellow-200 bg-yellow-50' : ''}>
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              {comm.priority >= 2 && <AlertTriangle className="h-4 w-4 text-red-600" />}
                              <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                              <CardTitle className="text-lg">{comm.title}</CardTitle>
                            </div>
                            <CardDescription className="flex items-center gap-3">
                              <span className="flex items-center gap-1">
                                <Calendar className="h-3 w-3" />
                                Pubblicato: {formatDate(comm.published_at)}
                              </span>
                              {comm.expiry_date && (
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  Scadenza: {formatDate(comm.expiry_date)}
                                  {String(comm.expiry_date).slice(0, 10) < new Date().toISOString().slice(0, 10) && (
                                    <Badge className="ml-1 bg-gray-100 text-gray-700">scaduta</Badge>
                                  )}
                                </span>
                              )}
                            </CardDescription>
                          </div>
                          {canPublish && (
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleArchive(comm.id)}
                                title="Archivia"
                              >
                                <Archive className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm whitespace-pre-wrap">{comm.content}</p>
                        {comm.attachments?.length > 0 && (
                          <div className="mt-3">
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Paperclip className="h-4 w-4" />
                              {comm.attachments.length} allegat{comm.attachments.length === 1 ? 'o' : 'i'}, solo consultazione
                            </div>
                            <ElencoAllegati condominiumId={condoId} ids={comm.attachments} />
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>

          {/* Draft Communications (Admin/Accountant only) */}
          {canEdit && draftComms.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-gray-600" />
                Bozze ({draftComms.length})
              </h2>
              <div className="grid gap-4">
                {draftComms.map((comm) => {
                  const typeInfo = COMM_TYPES[comm.comm_type] || COMM_TYPES.ALTRO;
                  return (
                    <Card key={comm.id} className="border-dashed">
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Badge className={typeInfo.color}>{typeInfo.label}</Badge>
                              <Badge variant="outline">Bozza</Badge>
                              <CardTitle className="text-lg">{comm.title}</CardTitle>
                            </div>
                            <CardDescription>
                              Creato: {formatDate(comm.created_at)}
                            </CardDescription>
                          </div>
                          <div className="flex gap-1">
                            {canEdit && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => apriModifica(comm)}
                                title="Modifica la bozza"
                              >
                                <Pencil className="h-4 w-4 text-blue-600" />
                              </Button>
                            )}
                            {canPublish && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handlePublish(comm.id)}
                                title="Pubblica"
                              >
                                <Send className="h-4 w-4 text-green-600" />
                              </Button>
                            )}
                            {canPublish && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(comm.id)}
                              >
                                <Trash2 className="h-4 w-4 text-red-600" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm whitespace-pre-wrap text-muted-foreground">{comm.content}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Archived Communications (Admin/Accountant only) */}
          {canEdit && archivedComms.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Archive className="h-5 w-5 text-slate-600" />
                Archivio ({archivedComms.length})
              </h2>
              <div className="grid gap-4">
                {archivedComms.slice(0, 5).map((comm) => {
                  const typeInfo = COMM_TYPES[comm.comm_type] || COMM_TYPES.ALTRO;
                  return (
                    <Card key={comm.id} className="opacity-75">
                      <CardHeader className="pb-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{typeInfo.label}</Badge>
                          <CardTitle className="text-base">{comm.title}</CardTitle>
                          <span className="text-sm text-muted-foreground">
                            {formatDate(comm.published_at)}
                          </span>
                        </div>
                      </CardHeader>
                    </Card>
                  );
                })}
                {archivedComms.length > 5 && (
                  <p className="text-sm text-muted-foreground text-center">
                    e altre {archivedComms.length - 5} comunicazioni archiviate
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
